import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Instagram } from "lucide-react";
import { themes, type Photographer } from "@/data/photographers";
import { fetchPhotographerBySlug } from "@/lib/photographers-source";
import { PortraitImage } from "@/components/PortraitImage";
import { FoundingMemberBadge } from "@/components/FoundingMemberBadge";
import { CreatorSelectedSection } from "@/components/CreatorSelectedSection";
import { fetchSelectedForCreator, type CreatorSelectedImage } from "@/lib/community-submissions";
import { sameImageUrl } from "@/lib/image-url";
import { supabase } from "@/integrations/supabase/client";
import { PhotoLightbox, type LightboxInfo } from "@/components/PhotoLightbox";

export const Route = createFileRoute("/photographers/$id")({
  loader: async ({ params }) => {
    const photographer = await fetchPhotographerBySlug(params.id);
    if (!photographer) throw notFound();
    const { data: ids } = await supabase
      .from("photographers")
      .select("id, user_id")
      .eq("slug", params.id)
      .maybeSingle();
    const selected = await fetchSelectedForCreator({
      userId: ids?.user_id ?? null,
      photographerDbId: ids?.id ?? null,
    });
    return { photographer, selected };
  },
  head: ({ loaderData }) => {
    const p = loaderData?.photographer;
    const descBase = p ? `${p.name}, Fotografie aus ${p.region}. ${p.bio}`.replace(/\s+/g, " ").trim() : "";
    const description = descBase.length > 160 ? descBase.slice(0, 157).trimEnd() + "…" : descBase;
    return {
      meta: p
        ? [
            { title: `${p.name}, OM Selected` },
            { name: "description", content: description },
            { property: "og:title", content: `${p.name}, OM Selected` },
            { property: "og:description", content: p.bio.slice(0, 160) },
            { property: "og:image", content: p.portrait },
            { property: "og:url", content: `https://omselected.art/photographers/${p.id}` },
          ]
        : [{ title: "Photographer, OM Selected" }],
      links: p ? [{ rel: "canonical", href: `https://omselected.art/photographers/${p.id}` }] : undefined,
      scripts: p
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "ProfilePage",
                url: `https://omselected.art/photographers/${p.id}`,
                inLanguage: "de-DE",
                mainEntity: {
                  "@type": "Person",
                  name: p.name,
                  description: p.bio,
                  image: p.portrait,
                  jobTitle: "Fotograf",
                  address: {
                    "@type": "PostalAddress",
                    addressLocality: p.city ?? p.region,
                    addressCountry: p.country ?? undefined,
                  },
                  email: p.email || undefined,
                  url: p.websiteUrl ?? undefined,
                  sameAs: [
                    p.websiteUrl,
                    p.instagramHandle ? `https://instagram.com/${p.instagramHandle}` : null,
                  ].filter(Boolean),
                },
              }),
            },
          ]
        : undefined,
    };
  },
  notFoundComponent: () => {
    const { t } = useTranslation();
    return (
      <div className="mx-auto max-w-[800px] px-5 py-32 text-center">
        <h1 className="serif text-4xl">{t("profile.notFound")}</h1>
        <Link to="/photographers" className="eyebrow inline-block mt-8 border-b border-foreground pb-1">{t("profile.back")}</Link>
      </div>
    );
  },
  errorComponent: ({ error }) => {
    const { t } = useTranslation();
    return (
      <div className="mx-auto max-w-[800px] px-5 py-32 text-center">
        <h1 className="serif text-4xl">{t("profile.error")}</h1>
        <p className="mt-4 text-muted-foreground">{error.message}</p>
      </div>
    );
  },
  component: PhotographerDetail,
});

function GalleryFigure({
  src,
  title,
  caption,
  alt,
  ariaLabel,
  onOpen,
}: {
  src: string;
  title: string;
  caption: string;
  alt: string;
  ariaLabel: string;
  onOpen: () => void;
}) {
  const [landscape, setLandscape] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const checkLandscape = (img: HTMLImageElement | null) => {
    if (img && img.naturalWidth > 0 && img.naturalWidth > img.naturalHeight * 1.1) {
      setLandscape(true);
    }
  };
  useEffect(() => {
    checkLandscape(imgRef.current);
  }, []);
  return (
    <figure className={landscape ? "col-span-2" : ""}>
      <button
        type="button"
        onClick={onOpen}
        className="block w-full overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground"
        aria-label={ariaLabel}
      >
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          onLoad={(e) => checkLandscape(e.currentTarget)}
          className="w-full h-auto cursor-zoom-in no-bw bg-muted"
        />
      </button>
      {(title || caption) && (
        <figcaption className="mt-3">
          {title && <div className="serif italic text-base">{title}</div>}
          {caption && <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{caption}</p>}
        </figcaption>
      )}
    </figure>
  );
}

function PhotographerDetail() {
  const { t } = useTranslation();
  const { photographer: p, selected } = Route.useLoaderData() as { photographer: Photographer; selected: CreatorSelectedImage[] };
  const [lightbox, setLightbox] = useState<{ items: LightboxInfo[]; index: number } | null>(null);
  const [galleryFilter, setGalleryFilter] = useState<string>("all");

  const makeInfo = (it: { src: string; title?: string; caption?: string; theme: string }): LightboxInfo => ({
    image: it.src,
    caption: it.caption || it.title || null,
    photographerName: p.name,
    photographerId: null,
    region: p.city ?? p.region,
    theme: themes.find((th) => th.id === it.theme)?.label ?? null,
    instagramHandle: p.instagramHandle ?? null,
    websiteUrl: p.websiteUrl ?? null,
    creatorType: "photographer" as const,
  });

  return (
    <div>
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 pt-10 pb-16 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <PortraitImage p={p} loading="eager" />
          </div>
          <div className="md:col-span-7 flex flex-col justify-between">
            <div>
              <Link to="/photographers" className="eyebrow text-muted-foreground hover:text-foreground">{t("profile.backPhotographers")}</Link>
              <div className="flex items-center gap-4 mt-6">
                <h1 className="serif text-5xl md:text-7xl leading-[0.95]">{p.name}</h1>
                {p.foundingMember && <FoundingMemberBadge />}
              </div>
              <div className="eyebrow text-muted-foreground mt-6">
                {p.themes.map((t) => themes.find((th) => th.id === t)?.label).join(" · ")} · {p.city ?? p.region}{p.country ? `, ${p.country}` : ""}
              </div>
              <p className="serif italic text-xl md:text-2xl mt-10 leading-snug max-w-xl">{p.bio}</p>
            </div>
            <div className="mt-12 grid grid-cols-2 gap-6 eyebrow text-muted-foreground border-t border-border pt-6">
              {(p.priceFrom > 0 || p.tfp || p.priceOnRequest) && (
                <div>
                  <div>{t("profile.priceFrom")}</div>
                  <div className="serif italic text-foreground text-lg mt-1 normal-case tracking-normal">
                    {[
                      p.priceFrom > 0 ? `${p.priceFrom.toLocaleString("de-DE")} €` : null,
                      p.tfp ? "TFP" : null,
                      p.priceOnRequest ? t("profile.onRequest") : null,
                    ].filter(Boolean).join(" · ")}
                  </div>
                </div>
              )}
              <div>
                <div>{t("profile.location")}</div>
                <div className="serif italic text-foreground text-lg mt-1 normal-case tracking-normal">
                  {[p.city ?? p.region, p.country].filter(Boolean).join(", ") || p.shoots}
                </div>
              </div>
              {p.equipment.length > 0 && (
                <div>
                  <div>{t("profile.equipment")}</div>
                  <div className="serif italic text-foreground text-lg mt-1 normal-case tracking-normal">{p.equipment.join(", ")}</div>
                </div>
              )}
              {p.email && (
                <div>
                  <div>{t("profile.contact")}</div>
                  <a href={`mailto:${p.email}`} className="serif italic text-foreground text-lg mt-1 normal-case tracking-normal block">{p.email}</a>
                </div>
              )}
              {(p.shootingSettings?.length ?? 0) > 0 && (
                <div>
                  <div>{t("profile.setting")}</div>
                  <div className="serif italic text-foreground text-lg mt-1 normal-case tracking-normal">{p.shootingSettings!.join(", ")}</div>
                </div>
              )}
              {(p.languages?.length ?? 0) > 0 && (
                <div>
                  <div>{t("profile.languages")}</div>
                  <div className="serif italic text-foreground text-lg mt-1 normal-case tracking-normal">{p.languages!.join(", ")}</div>
                </div>
              )}
            </div>
            {(p.websiteUrl || p.instagramHandle) && (
              <div className="mt-8 flex flex-wrap gap-3 eyebrow">
                {p.websiteUrl && (
                  <a
                    href={p.websiteUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 border border-foreground px-5 py-3 hover:bg-foreground hover:text-background transition-colors"
                  >
                    <span aria-hidden>↗</span> {t("profile.website")}
                  </a>
                )}
                {p.instagramHandle && (
                  <a
                    href={`https://instagram.com/${p.instagramHandle}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 border border-foreground px-5 py-3 hover:bg-foreground hover:text-background transition-colors"
                  >
                    <Instagram aria-hidden className="w-4 h-4" /> @{p.instagramHandle}
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-20">
          <h2 className="eyebrow text-muted-foreground mb-8">{t("profile.gallery")}</h2>
          {(() => {
            const tags = p.galleryThemes ?? [];
            const galleryItems = p.gallery.map((src, i) => ({
              src,
              theme: tags[i] ?? "open",
              title: p.galleryTitles?.[i] ?? "",
              caption: p.galleryCaptions?.[i] ?? "",
              index: i,
              key: `gallery-${i}`,
              number: i + 1,
            }));
            const presentThemeIds = themes
              .map((th) => th.id)
              .filter((id) => galleryItems.some((it) => it.theme === id));
            const selectedGalleryItems = galleryItems.filter((it) =>
              selected.some((s) => sameImageUrl(s.image_url, it.src))
            );
            const selectedFilterItems = selected.length > 0
              ? selected.map((item, i) => {
                  const galleryItem = galleryItems.find((it) => sameImageUrl(it.src, item.image_url));
                  return galleryItem ?? {
                    src: item.image_url,
                    theme: "selected",
                    title: item.caption ?? "",
                    caption: item.caption ?? "",
                    index: i,
                    key: `selected-${item.id}`,
                    number: i + 1,
                  };
                })
              : selectedGalleryItems;
            const hasSelectedFilter = selectedFilterItems.length > 0;
            const visibleItems = galleryFilter === "all"
              ? galleryItems
              : galleryFilter === "selected"
              ? selectedFilterItems
              : galleryItems.filter((it) => it.theme === galleryFilter);
            const showFilters = galleryItems.length > 0;
            return (
              <>
                {showFilters && (
                  <div className="flex flex-wrap gap-2 mb-8" role="group" aria-label={t("profile.gallery")}>
                    <button
                      type="button"
                      onClick={() => setGalleryFilter("all")}
                      aria-pressed={galleryFilter === "all"}
                      className={`eyebrow border px-4 py-2 transition-colors ${galleryFilter === "all" ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"}`}
                    >
                      {t("profile.filterAll")}
                    </button>
                    {hasSelectedFilter && (
                      <button
                        type="button"
                        onClick={() => setGalleryFilter("selected")}
                        aria-pressed={galleryFilter === "selected"}
                        className={`eyebrow border px-4 py-2 transition-colors ${galleryFilter === "selected" ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"}`}
                      >
                        {t("profile.filterSelected")}
                      </button>
                    )}
                    {presentThemeIds.map((id) => {
                      const label = themes.find((th) => th.id === id)?.label ?? id;
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setGalleryFilter(id)}
                          aria-pressed={galleryFilter === id}
                          className={`eyebrow border px-4 py-2 transition-colors ${galleryFilter === id ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"}`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 items-start">
                  {visibleItems.map((it, idx) => (
                    <GalleryFigure
                      key={it.key}
                      src={it.src}
                      title={it.title}
                      caption={it.caption}
                      alt={it.title || `${p.name}, ${it.number}`}
                      ariaLabel={t("profile.zoomAria", { n: it.number })}
                      onOpen={() => setLightbox({ items: visibleItems.map(makeInfo), index: idx })}
                    />
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      </section>

      {p.gallery.length > 0 && (
        <section className="border-b border-border">
          <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-20">
            <h2 className="eyebrow text-muted-foreground mb-8">{t("profile.selectedWorks")}</h2>
            <div className="space-y-12">
              {themes.map((theme) => {
                const tags = p.galleryThemes ?? [];
                const items = p.gallery
                  .map((src, i) => ({
                    src,
                    theme: tags[i] ?? "open",
                    title: p.galleryTitles?.[i] ?? "",
                    caption: p.galleryCaptions?.[i] ?? "",
                  }))
                  .filter((x) => x.theme === theme.id);
                if (items.length === 0) return null;
                return (
                  <div key={theme.id}>
                    <div className="flex items-baseline justify-between mb-4 border-b border-border pb-3">
                      <h2 className="serif text-3xl md:text-4xl">{theme.label}</h2>
                      <span className="eyebrow text-muted-foreground">{items.length} {items.length === 1 ? "Bild" : "Bilder"}</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 items-start">
                      {items.map((it, i) => (
                        <GalleryFigure
                          key={`${theme.id}-${i}`}
                          src={it.src}
                          title={it.title}
                          caption={it.caption}
                          alt={it.title || `${p.name}, ${theme.label} ${i + 1}`}
                          ariaLabel={t("profile.zoomAriaTheme", { label: theme.label, n: i + 1 })}
                          onOpen={() => setLightbox({ items: items.map(makeInfo), index: i })}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <CreatorSelectedSection items={selected} name={p.name} />

      <section>
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-20 text-center">
          <h2 className="serif text-3xl md:text-4xl">{t("profile.workWith", { name: p.name.split(" ")[0] })}</h2>
          <div className="mt-8 flex justify-center gap-3 eyebrow">
            <a href={`mailto:${p.email}`} className="border border-foreground px-5 py-3 hover:bg-foreground hover:text-background transition-colors">{t("profile.writeDirect")}</a>
            <Link to="/brief" className="border border-border px-5 py-3 hover:border-foreground transition-colors">{t("profile.sendRequest")}</Link>
          </div>
        </div>
      </section>

      <PhotoLightbox
        info={lightbox ? lightbox.items[lightbox.index] : null}
        onClose={() => setLightbox(null)}
        onPrev={lightbox && lightbox.index > 0 ? () => setLightbox((s) => (s ? { ...s, index: s.index - 1 } : s)) : undefined}
        onNext={lightbox && lightbox.index < lightbox.items.length - 1 ? () => setLightbox((s) => (s ? { ...s, index: s.index + 1 } : s)) : undefined}
        neighborImages={lightbox ? [lightbox.items[lightbox.index - 1]?.image, lightbox.items[lightbox.index + 1]?.image] : undefined}
      />
    </div>
  );
}